<template>
    <VField
        v-if="uid"
        ref="validationProvider"
        v-slot="{errors, valid, field, meta}"
        :mode="validationMode"
        :rules="{required: !!required, ...computedRules}"
        :name="inputName"
        :label="label || name"
        :vid="generatedVid"
        v-bind="$attrs"
        :immediate="validateImmediate">
        <div
            v-bind="$attrs"
            class="form-group"
            :class="[{'is-invalid': meta.touched && errors[0]}, {'mb-2': smallMargin}, {'mb-0': noMargin}, generatedVid, (parentUuid ? parentUuid + '.' : '') + (name || label || generatedVid)]">
            <slot name="label">
                <label
                    v-if="label && !hideLabel"
                    :class="labelClasses">
                    <span>{{ label }} </span>
                    <BaseTooltip
                        v-if="hint"
                        :content="hint">
                        <font-awesome-icon
                            class="text-muted"
                            icon="question-circle"
                            fixed-width />
                    </BaseTooltip>
                    <span v-if="required">*</span>
                </label>
            </slot>
            <div
                :class="[
                    {'input-group': true},
                    {'focused': focused},
                    {'input-group-alternative': alternative},
                    {'has-label': label || $slots.label},
                    inputGroupClasses,
                ]">
                <div
                    v-if="prependIcon || $slots.prepend"
                    class="input-group-prepend"
                    :class="[{'pointer': prependPointer}, {'d-none d-sm-flex': !showIconOnMobile}]"
                    @click="$emit('prependClick', $event)">
                    <span class="input-group-text">
                        <slot name="prepend">
                            <font-awesome-icon
                                v-if="prependIcon"
                                :icon="prependIcon"
                                fixed-width />
                        </slot>
                    </span>
                </div>
                <slot
                    v-bind="{...slotData, meta, field: {...field, modelValue: field.value, 'update:modelValue': field['update:value']}, validated: meta.touched, valid: valid}">
                    <input
                        v-bind="field"
                        ref="input"
                        :type="type"
                        :valid="valid"
                        :disabled="disabled || readonly"
                        :placeholder="placeholder"
                        class="form-control"
                        :class="[
                            {'is-valid': valid && meta.touched && successMessage},
                            {'is-invalid': errors[0] && meta.touched},
                            {'hide-arrows': hideArrows},
                            inputClasses
                        ]"
                        v-on="listeners">
                </slot>
                <div
                    v-if="appendIcon || $slots.append"
                    class="input-group-append"
                    :class="[{'pointer': appendPointer}, {'d-none d-sm-flex': !showIconOnMobile}]"
                    @click="$emit('appendClick', $event)">
                    <span class="input-group-text">
                        <slot name="append">
                            <font-awesome-icon
                                v-if="appendIcon"
                                :icon="appendIcon"
                                fixed-width />
                        </slot>
                    </span>
                </div>
                <slot name="infoBlock" />
            </div>
            <slot name="success">
                <div
                    v-if="valid && meta.touched"
                    class="valid-feedback">
                    {{ successMessage }}
                </div>
            </slot>
            <slot
                name="error"
                :errors="errors">
                <div
                    v-if="errors[0]"
                    class="invalid-feedback"
                    style="display: block;">
                    {{ errors[0] }}
                </div>
            </slot>
        </div>
    </VField>
</template>
<script>
import BaseTooltip from '../BaseTooltip'
import symbols from "@core/util/symbols";
import {v4 as uuidv4} from "uuid";

export default {
    name: "BaseInput",
    components: {BaseTooltip},
    inheritAttrs: false,
    inject: {parentUuid: {from: symbols.nestingFormKey, default: null}},
    props: {
        required: {
            type: [Boolean, Number],
            description: "Whether input is required (adds an asterix *)"
        },
        group: {
            type: Boolean,
            description: "Whether input is an input group (manual override in special cases)"
        },
        alternative: {
            type: Boolean,
            description: "Whether input is of alternative layout"
        },
        disabled: {
            type: Boolean,
            default: false,
            description: "Whether input is disabled"
        },
        readonly: {
            type: Boolean,
            default: false,
            description: "Whether input is disabled"
        },
        label: {
            type: String,
            description: "Input label (text before input)"
        },
        placeholder: {
            type: String,
            description: "Input placeholder"
        },
        hint: {
            type: String,
            description: "Input hint text"
        },
        error: {
            type: String,
            description: "Input error (below input)"
        },
        successMessage: {
            type: String,
            description: "Input success message",
            default: ''
        },
        labelClasses: {
            type: String,
            description: "Input label css classes",
            default: "form-control-label"
        },
        inputClasses: {
            type: String,
            description: "Input css classes"
        },
        inputGroupClasses: {
            type: String,
            description: "Input group css classes"
        },
        type: {
            type: String,
            description: "Input type",
            default: "text"
        },
        appendIcon: {
            type: [String, Array],
            description: "Append icon (right)"
        },
        prependIcon: {
            type: [String, Array],
            description: "Prepend icon (left)"
        },
        showIconOnMobile: {
            type: Boolean,
            default: true
        },
        appendPointer: {
            type: Boolean,
            default: false
        },
        prependPointer: {
            type: Boolean,
            default: false
        },
        rules: {
            type: [String, Array, Object],
            description: 'Vee validate validation rules',
            default: ''
        },
        name: {
            type: String,
            description: 'Input name (used for validation)',
            default: ''
        },
        validationMode: {
            type: String,
            description: 'Mode aggressive|lazy|passive|eager',
            default: 'aggressive'
        },
        validateImmediate: {
            type: Boolean,
            default: false
        },
        smallMargin: {
            type: Boolean,
            description: 'Removes large Margin Bottom',
            default: false
        },
        noMargin: {
            type: Boolean,
            description: 'Removes large Margin Bottom',
            default: false
        },
        vid: {
            type: String,
            default: null
        },
        hideArrows: {
            type: Boolean,
            default: false
        },
        focusOnMounted: {
            type: Boolean,
            default: false
        },
        hideLabel: {
            type: Boolean,
            default: false
        },
        amosWebId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            focused: false,
            uid: uuidv4()
        };
    },
    computed: {
        inputName() {
            const uid = this.uid
            return (this.parentUuid ? this.parentUuid + '.' : '') + (this.name || this.label) + (uid ? ' (' + uid + ')' : '')
        },
        computedRules() {
            if (typeof this.rules === 'object') {
                return this.rules
            } else if (typeof this.rules === 'string') {
                if (this.rules !== "")
                    return {[this.rules]: true}
            } else if (Array.isArray(this.rules)) {
                const rules = {}
                for (const rule of this.rules) {
                    rules[rule] = true
                }
                return rules
            }

            return {}
        },
        listeners() {
            return {
                input: this.updateValue,
                focus: this.onFocus,
                blur: this.onBlur
            };
        },
        slotData() {
            return {
                focused: this.focused,
                error: this.error,
                ...this.listeners
            };
        },
        hasIcon() {
            const {append, prepend} = this.$slots;
            return (
                append !== undefined ||
                prepend !== undefined ||
                this.appendIcon !== undefined ||
                this.prependIcon !== undefined ||
                this.group
            );
        },
        generatedVid() {
            if (this.vid) return this.vid
            const uid = this.uid
            return (this.$route.name + ':' + this.name + (uid?.toString().replace(/-/g, '_') ?? "")).replace(/\s+/g, '_')
        },
        amosWebInputId() {
            if (this.amosWebId) return this.amosWebId
            if (this.name) return this.name.replace(/\s+/g, '')
            if (this.label) return this.label.replace(/\s+/g, '')
            return null
        }
    },
    methods: {
        focus() {
            this.$refs.input?.focus()
        },
        updateValue(evt) {
            const value = evt.target.value;
            this.$emit("input", value);
            this.$emit("update:modelValue", value);
            this.$nextTick(() => {
                this.$refs.validationProvider?.validate()
            })
        },
        onFocus(evt) {
            this.focused = true;
            this.$emit("focus", evt);
        },
        onBlur(evt) {
            this.focused = false;
            this.$emit("blur", evt);
        }
    },
    mounted() {
        if (this.focusOnMounted) {
            this.$nextTick(() => {
                this.focus()
            })
        }
    }
};
</script>
<style>

.input-group-text {
    padding-top: 0;
    padding-bottom: 0;
}

.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield;
}

.input-group {
    flex-wrap: nowrap;
}

</style>
